import { BuildingSkyscraperIcon, Button, CheckIcon, TextInput, UserIcon, notification } from "@datagame/ui";
import { Field, Form, Formik } from "formik";
import { object, string } from "yup";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { UserUpdateInput } from "../../../../gql/graphql";
import { USER_UPDATE } from "../../../../graphql/mutations/UserMutations";
import { FieldWrapper } from "../../../shared/ui/FieldWrapper";
import { ErrorMessage } from "../../../shared/ui/ErrorMessage";
import { UserInterface } from "../ProfileView";

import styles from "./UserForm.module.scss";

export interface UserFormProps {
  user: UserInterface;
  isActive: boolean;
  onSubmit: () => void;
}

export const UserForm = ({ user, isActive, onSubmit }: UserFormProps) => {
  const { t } = useTranslation();

  const [userUpdate, { error }] = useMutation(USER_UPDATE);

  const validationSchema = object({
    firstName: string().required(),
    lastName: string().required(),
  });

  const initialValues: UserUpdateInput = {
    firstName: user.firstName,
    lastName: user.lastName,
    companyName: user.organization?.name,
  };

  const handleSubmit = (values: UserUpdateInput) => {
    const variables = {
      data: {
        firstName: values.firstName,
        lastName: values.lastName,
        companyName: values.companyName,
      },
    };
    if (variables.data.companyName === "") {
      delete variables.data.companyName;
    }
    userUpdate({
      variables,
      onCompleted: () => {
        notification.success({
          title: t("profile:profileSection.form.successTitle"),
          description: t("profile:profileSection.form.successMessage"),
        });
      },
    });

    onSubmit();
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ errors }) => (
        <Form className={styles.form}>
          {error && <ErrorMessage>{error.message}</ErrorMessage>}
          <FieldWrapper label={t("profile:profileSection.form.firstName")} name="firstName">
            <Field
              placeholder="Please enter your first name..."
              name="firstName"
              size="small"
              as={TextInput}
              icon={<UserIcon />}
              error={errors.firstName}
              readOnly={!isActive}
            />
          </FieldWrapper>

          <FieldWrapper label={t("profile:profileSection.form.lastName")} name="lastName">
            <Field
              placeholder="Please enter your last name..."
              name="lastName"
              size="small"
              as={TextInput}
              icon={<UserIcon />}
              error={errors.lastName}
              readOnly={!isActive}
            />
          </FieldWrapper>

          <FieldWrapper label={t("profile:profileSection.form.companyName")} name="companyName">
            <Field
              placeholder="Please enter your company name..."
              name="companyName"
              size="small"
              as={TextInput}
              icon={<BuildingSkyscraperIcon />}
              error={errors.companyName}
              readOnly={!isActive}
            />
          </FieldWrapper>

          {isActive && (
            <Button type="submit" icon={<CheckIcon />} iconSide="left" className={styles.submitButton}>
              {t("profile:change")}
            </Button>
          )}
        </Form>
      )}
    </Formik>
  );
};
